import { z } from "zod";
import { Product } from "./product.js";
import { Labels } from "./vocabulary.js";
import {
  addDays,
  addStops,
  calendarForRow,
  dayNo,
  mdOf,
  normalizeSchedule,
  scheduleBriefOf,
  setTaskState,
  workDatesOf,
  workDaysBetween,
  SCHEDULE_LIMITS,
  ScheduleReport,
  ScheduleReportKind,
  ScheduleReportReason,
  ScheduleReportStatus,
  type ScheduleDoc,
  type ScheduleRow,
  type ScheduleRowKind,
  type ScheduleStop,
  type WorkCalendar,
} from "./schedule.js";

// ---------------------------------------------------------------------------
// 工程管理の4回目（#103、docs/39 §6）：協力会社へのリンクと報告
//   - リンクは取引先（業者）ごとに1本。ログインなし・期限つき（30／90／180日）。業者はそのリンクで 自分の工程を見て
//     「終わった」「雨で中止」「遅れる」「写真」を送る。工程表は直接変えない（確かめてから反映する＝決めごと6）
//   - 反映の計算は applyReport 1か所（API が工程表を1つのトランザクションで直して版を＋1。案件の工程表で同じことをしたときと同じ）
//   - 協力会社のページの 今日・次・済み の分け方は partnerTasksOf 1か所（API が返す前にかける）
// ---------------------------------------------------------------------------

/** 上限（docs/39 §6.2・§6.5） */
export const PARTNER_LINK_LIMITS = {
  /** 使える期間（日）。既定は 90 */
  days: [30, 90, 180] as const,
  defaultDays: 90,
  /** 期限の何日前から「リンク 12/29 まで」と出すか */
  warnDays: 14,
  /** 1日（日本時間）に1本のリンクから送れる報告・写真 */
  reportsPerDay: 30,
  photosPerDay: 30,
  /** 1回の写真の報告の枚数 */
  photosPerReport: 5,
  /** ひとこと・見送るわけ */
  note: 60,
  /** 遅れる日数 */
  delayMax: 30,
  /** 同じ IP から1分に（公開の API すべて） */
  ipPerMinute: 60,
  /** 表示用の写真（JPEG）：2MB・長辺 2000px まで。小さい絵：200KB・長辺 400px まで */
  displayBytes: 2 * 1024 * 1024,
  displayPx: 2000,
  thumbBytes: 200 * 1024,
  thumbPx: 400,
  /** 画面で小さくする大きさ（表示用 1600px・品質 0.8、小さい絵 400px） */
  resizeDisplayPx: 1600,
  resizeThumbPx: 400,
  /** 協力会社のページ：次の予定は 60日先まで、済んだ工程は 7日前まで、送った報告は 14日前まで */
  nextDays: 60,
  doneDays: 7,
  overdueDays: 30,
  reportDays: 14,
  /** 反映・見送りの記録は 30日分 */
  historyDays: 30,
  /** 見送った・取り消した報告の写真を消すまでの日数 */
  photoPurgeDays: 90,
  /** 開いた回数を数え直す間（1時間に1回まで） */
  openTouchMinutes: 60,
} as const;

/** token：32バイトの乱数を base64url（43字）。形が違えば API は 404（DB まで行かない） */
export const PARTNER_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

export const REPORT_REASON_LABEL: Record<ScheduleReportReason, string> = {
  material: "材料待ち",
  people: "人手不足",
  weather: "天候",
  before: "前の{task}",
  other: "その他",
};
export const REPORT_REASONS = ScheduleReportReason.options;

/** 画面に出す文（1か所） */
export const PARTNER_LINK_MSG = {
  /* ---- リンクを送る（L1） ---- */
  linkTitle: (name: string) => `${name}へリンク送信`,
  linkLead: (name: string) =>
    `ログイン不要で、${name}の{task}のみ閲覧できるリンクです。今日の予定の確認と、「完了」「{pw:stopRain}」「遅延報告」「写真」の送信ができます。届いた報告は、確認後に{schedule}へ反映します。{siteShort}名は表示されます。`,
  linkDays: "有効期間",
  daysOption: (n: number) => `${n}日`,
  until: (md: string) => `${md} まで`,
  showAddress: "{siteAddress}の表示",
  showContact: "{siteShort}担当・電話の表示",
  create: "リンク作成",
  created: "リンクを作成しました",
  copy: "コピー",
  copied: "コピーしました",
  copyFailed: "コピーできませんでした。リンクの長押しでコピーできます",
  sendLine: "LINE で送信",
  sendMail: "メールで送信",
  sendSms: "SMS で送信",
  noMail: "担当者のメールアドレスが未登録です",
  noSms: "担当者の携帯番号が未登録です",
  lastOpened: (at: string, n: number) => `最終閲覧：${at}（${n}回）`,
  neverOpened: "未閲覧",
  revoke: "リンク停止",
  revokeTitle: "リンク停止",
  revokeBody: (name: string) => `${name}に送信したリンクは無効になります。再送には新しいリンクの作成が必要です。`,
  revoked: "リンクを停止しました",
  renew: "リンク再発行",
  renewTitle: "リンク再発行",
  renewBody: "前のリンクは無効になります。新しいリンクの再送が必要です。",
  renewed: "リンクを再発行しました（再送が必要です）",
  updated: "リンク設定を変更しました",
  close: "閉じる",
  shareSubject: (company: string) => `${company}の{schedule}のリンク`,
  shareBody: (company: string, url: string, until: string) =>
    `${company}です。{schedule}のリンクをお送りします。本日の予定をご確認のうえ、作業が終わりましたら「完了」を押してください。${url}（${until}まで）`,
  linkChip: "リンク",
  linkChipUntil: (md: string) => `リンク ${md} まで`,
  linkChipExpired: "リンク 期限切れ",
  menuSendLink: (name: string) => `${name || "{contractor}"}へリンク送信`,
  notPartner: "リンクを送信できるのは、担当が取引先の{task}のみです",
  linkNotFound: "リンクがありません",

  /* ---- 協力会社のページ（D・D2・D3） ---- */
  honorific: (name: string) => `${name} 様`,
  todayTitle: "今日の予定",
  from: (company: string) => `${company}から`,
  todayChip: "今日",
  /** 今日がその{task}の休み（#293） */
  restTodayChip: "今日は休み",
  /** カードの日のメモ（#299）：{task}のメモと、日付の見出しのメモ（{siteShort}のメモ） */
  dayNotesTitle: "メモ",
  siteNote: "{siteShort}のメモ",
  dayOf: (n: number, total: number) => `${n}日目／${total}日`,
  lastDay: "最終日",
  finished: "済み",
  overdue: (md: string) => `予定は ${md} まで`,
  map: "地図",
  phone: "電話",
  staff: "{siteShort}の担当",
  done: "完了",
  stop: "{pw:stopRain}",
  delay: "遅延報告",
  photo: "写真",
  noToday: "今日の予定はありません",
  nextTitle: "次の予定",
  doneTitle: "完了した{task}",
  footer: (md: string) => `ログイン不要のリンク・${md} まで`,
  footerNote: "このリンクを第三者に渡さないでください",
  sent: (company: string) => `送信しました。${company}が確認します`,
  statusPending: (kind: string, time: string) => `「${kind}」を送信しました・確認待ち ${time}`,
  statusApplied: (kind: string) => `「${kind}」を反映しました`,
  statusDismissed: (kind: string) => `「${kind}」は見送りました`,
  withdraw: "報告取消",
  withdrawn: "取り消しました",
  delayTitle: "遅延報告",
  delayHowMany: "あと何日かかりますか",
  delayMore: "4日以上",
  delayDays: (n: number) => `${n}日`,
  reasonTitle: "理由",
  noteTitle: "ひとこと（任意）",
  sendFoot: (company: string) => `送信後、${company}が確認してから{schedule}に反映します`,
  send: "送信",
  cancel: "取消",
  pickReason: "理由を選択してください",
  photoTitle: "写真送信",
  photoPick: "撮影・選択",
  photoLeft: (n: number) => `${PARTNER_LINK_LIMITS.photosPerReport}枚まで（あと${n}枚）`,
  photoNote: "写真は縮小して送信します。撮影場所の情報は送信しません",
  photoSend: (n: number) => `${n}枚送信`,
  photoSending: (i: number, n: number) => `送信中 ${i}／${n}`,
  photoRemove: "写真削除",
  photoBroken: "この写真は読み込めませんでした",
  stopConfirm: "今日のこの{task}を中止にする案を送ります",
  gone: (company: string) => `このリンクは無効です（期限切れか停止）。${company}にお問い合わせください`,
  notFound: "このリンクは無効です",
  tooMany: "本日の送信の上限に達しました",
  notStarted: "開始日から押せます",
  notWorkDay: "今日はこの{task}の作業日ではありません",
  alreadyDone: "この{task}は完了済みです",
  duplicate: "同じ報告が確認待ちです",
  rowNotFound: "{task}が見つかりません",
  reportNotFound: "報告が見つかりません",
  notPending: "確認待ちの報告のみ取り消せます",
  photoType: "写真は JPEG のみ送信できます",
  photoTooBig: "写真が大きすぎます",
  photoFull: `写真は1回に${PARTNER_LINK_LIMITS.photosPerReport}枚までです`,

  /* ---- 会社の画面（R1・案件の工程表・メニュー・ホーム） ---- */
  reportsTitle: "{contractor}からの報告",
  pendingCount: (n: number) => `確認待ち ${n}件`,
  history: "反映・見送り記録",
  historyEmpty: `直近${PARTNER_LINK_LIMITS.historyDays}日の反映・見送りはありません`,
  fold: "折りたたみ",
  unfold: "展開",
  more: (n: number) => `ほか ${n}件`,
  apply: "反映",
  dismiss: "見送り",
  dismissNote: "見送りの理由（任意）",
  applied: "反映しました",
  dismissed: "見送りました",
  reopen: "確認待ちに変更",
  reopened: "確認待ちに戻しました",
  openSchedule: "{schedule}表示",
  stale: "反映できません（{task}が変わりました）",
  notPendingCompany: "確認待ちの報告ではありません",
  kindDone: "完了",
  kindStop: "{pw:stopRain}",
  kindDelay: (n: number) => `遅延 ${n}日`,
  kindPhoto: (n: number) => `写真 ${n}枚`,
  noticeLine: (n: number) => `{contractor}からの報告 ${n}件`,
  noticeItem: (partner: string, what: string) => `${partner}「${what}」`,
  see: "表示",
  rowReport: "報告",
  rowPhotos: (n: number) => `写真 ${n}`,
  photoBy: (partner: string, at: string) => `${partner}・${at}`,
  prevPhoto: "前の写真",
  nextPhoto: "次の写真",
  decidedApplied: (who: string, at: string) => `${who ? `${who}が` : ""}${at} に反映`,
  decidedDismissed: (who: string, at: string) => `${who ? `${who}が` : ""}${at} に見送り`,
  homeCard: "{contractor}からの報告",
} as const;

/** 報告の種類の短い文（「完了」「雨で中止」「遅れる 2日」「写真 3枚」）。協力会社のページの「送りました」の行もこれ（S-00009：ボタンは「遅延報告」、報告の名前は「遅れる n日」のまま） */
export function reportKindText(r: { kind: ScheduleReportKind; delayDays?: number | null; photoCount?: number }): string {
  switch (r.kind) {
    case "done":
      return PARTNER_LINK_MSG.kindDone;
    case "stop":
      return PARTNER_LINK_MSG.kindStop;
    case "delay":
      return PARTNER_LINK_MSG.kindDelay(r.delayDays ?? 0);
    case "photo":
      return PARTNER_LINK_MSG.kindPhoto(r.photoCount ?? 0);
  }
}
/** 知らせの行の「2日遅れる」 */
export function reportNoticeText(r: { kind: ScheduleReportKind; delayDays?: number | null; photoCount?: number }): string {
  return r.kind === "delay" ? `${r.delayDays ?? 0}日遅延` : reportKindText(r);
}

/* ---------------- リンク（ログインあり） ---------------- */

export const PartnerLinkDays = z.union([z.literal(30), z.literal(90), z.literal(180)]);
export const PartnerLinkCreate = z.object({
  days: PartnerLinkDays.default(PARTNER_LINK_LIMITS.defaultDays),
  showAddress: z.boolean().default(true),
  showContact: z.boolean().default(true),
});
export type PartnerLinkCreate = z.input<typeof PartnerLinkCreate>;
export const PartnerLinkPatch = z.object({ days: PartnerLinkDays.optional(), showAddress: z.boolean().optional(), showContact: z.boolean().optional() });
export type PartnerLinkPatch = z.infer<typeof PartnerLinkPatch>;

/** GET・POST・PATCH /partners/:id/schedule-link の答え。link はまだ作っていない（止めた）なら null */
export const PartnerLinkView = z.object({
  partner: z.object({ id: z.string().uuid(), name: z.string(), fullName: z.string() }),
  /** 送る文の頭の会社名（「株式会社」などを外した名前） */
  company: z.string(),
  /** 主の担当者のメール・携帯（無ければ null＝そのボタンは押せない） */
  mailTo: z.string().nullable(),
  smsTo: z.string().nullable(),
  link: z
    .object({
      url: z.string(),
      expiresAt: z.string(),
      expired: z.boolean(),
      showAddress: z.boolean(),
      showContact: z.boolean(),
      createdAt: z.string(),
      lastOpenedAt: z.string().nullable(),
      openCount: z.number().int(),
    })
    .nullable(),
});
export type PartnerLinkView = z.infer<typeof PartnerLinkView>;

/** 画面に出すリンク：token の真ん中を「…」で縮める（https://genbaryoku.jp/p/k3Vq9xT2…8wZ） */
export function shortLinkText(url: string): string {
  const m = /^(.*\/p\/)([A-Za-z0-9_-]+)$/.exec(url);
  if (!m || m[2]!.length <= 12) return url;
  return `${m[1]}${m[2]!.slice(0, 8)}…${m[2]!.slice(-3)}`;
}
/** 全体の工程表の業者の行のしるし：期限の 14日前からは「リンク 12/29 まで」、切れたら「リンク 期限切れ」、ほかは「リンク」 */
export function linkChipText(expiresAt: string | null, now: Date = new Date()): string {
  if (!expiresAt) return PARTNER_LINK_MSG.linkChip;
  const t = new Date(expiresAt).getTime();
  if (t <= now.getTime()) return PARTNER_LINK_MSG.linkChipExpired;
  if (t - now.getTime() <= PARTNER_LINK_LIMITS.warnDays * 86_400_000) return PARTNER_LINK_MSG.linkChipUntil(mdJst(expiresAt));
  return PARTNER_LINK_MSG.linkChip;
}
/** 日時 → 日本時間の「12/29」 */
export function mdJst(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 9 * 3600_000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}
/** 日時 → 日本時間の「9/30 17:52」 */
export function mdHmJst(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 9 * 3600_000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}
/** 日時 → 日本時間の「17:52」 */
export function hmJst(iso: string): string {
  return mdHmJst(iso).split(" ")[1]!;
}
/** 日時 → 日本時間の日付（YYYY-MM-DD） */
export function ymdJst(iso: string | Date): string {
  const d = new Date((typeof iso === "string" ? new Date(iso) : iso).getTime() + 9 * 3600_000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/* ---------------- 協力会社のページ（ログインなし） ---------------- */

/** 業者が送る報告（POST /p/:token/reports）。日数とわけは「遅れる」だけ（遅れるには両方） */
export const PartnerReportCreate = z
  .object({
    rowId: z.string().uuid(),
    kind: ScheduleReportKind,
    delayDays: z.number().int().min(1).max(PARTNER_LINK_LIMITS.delayMax).nullable().optional(),
    reason: ScheduleReportReason.nullable().optional(),
    note: z.string().trim().max(PARTNER_LINK_LIMITS.note).nullable().optional(),
  })
  .superRefine((x, ctx) => {
    if (x.kind === "delay") {
      if (x.delayDays == null) ctx.addIssue({ code: "custom", path: ["delayDays"], message: PARTNER_LINK_MSG.delayHowMany });
      if (x.reason == null) ctx.addIssue({ code: "custom", path: ["reason"], message: PARTNER_LINK_MSG.pickReason });
    } else if (x.delayDays != null || x.reason != null) {
      ctx.addIssue({ code: "custom", path: ["kind"], message: "日数と理由は「遅延」のみです" });
    }
  });
export type PartnerReportCreate = z.input<typeof PartnerReportCreate>;

/** 写真を1枚ずつ（POST /p/:token/reports/:id/photos）。表示用と小さい絵の JPEG を base64 で */
export const PartnerPhotoUpload = z.object({
  display: z.string().min(4).max(Math.ceil((PARTNER_LINK_LIMITS.displayBytes * 4) / 3) + 8),
  thumb: z.string().min(4).max(Math.ceil((PARTNER_LINK_LIMITS.thumbBytes * 4) / 3) + 8),
});
export type PartnerPhotoUpload = z.infer<typeof PartnerPhotoUpload>;

/** 協力会社のページの1工程（金額・ほかの業者・社内メモ・施主の連絡先は持たない） */
export const PartnerTask = z.object({
  rowId: z.string().uuid(),
  groupName: z.string(),
  name: z.string(),
  start: z.string(),
  end: z.string(),
  days: z.number().int(),
  crew: z.number().int().nullable(),
  progress: z.number().int(),
  /** 稼働日（休み・中止を入れた暦。区切りの棒） */
  workDates: z.array(z.string()),
  /** 今日が何日目か（今日が稼働日のときだけ） */
  dayIndex: z.number().int().nullable(),
  /** 今日で終わり */
  lastDay: z.boolean(),
  /** 終わりの日を過ぎたのに済んでいない */
  overdue: z.boolean(),
  /** 始まった（「終わった」を押せる） */
  started: z.boolean(),
  /** 今日が稼働日（「雨で中止」を押せる） */
  workToday: z.boolean(),
  /** 始まっていて終わっていない{task}で、今日が休み（休みの曜日・祝日・日ごとの休み・{siteShort}の休み・中止。#293）＝札「今日は休み」 */
  restToday: z.boolean().default(false),
  /** 今日より後の最初の稼働日（次の予定の並び） */
  nextDate: z.string().nullable(),
  /**
   * 日のメモ（#299、docs/102 §5.10）：その{task}のメモと日付の見出しのメモ（site＝{siteShort}のメモ）。
   * 今日から{task}の終わりまでの日（範囲の外の{task}のメモは出さない）・日付の順・20 件まで
   */
  dayNotes: z.array(z.object({ day: z.string(), text: z.string(), site: z.boolean() })).default([]),
  /** リンクの「現場の住所を見せる」が入のときだけ */
  address: z.string().nullable(),
  /** リンクの「現場の担当と電話を見せる」が入のときだけ */
  staffName: z.string().nullable(),
  phone: z.string().nullable(),
});
export type PartnerTask = z.infer<typeof PartnerTask>;

/** 協力会社のページの送った報告 */
export const PartnerReport = z.object({
  id: z.string().uuid(),
  rowId: z.string().uuid().nullable(),
  kind: ScheduleReportKind,
  workDate: z.string(),
  delayDays: z.number().int().nullable(),
  reason: ScheduleReportReason.nullable(),
  note: z.string().nullable(),
  status: ScheduleReportStatus,
  /** 見送ったわけ（見送ったときだけ） */
  decidedNote: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  photos: z.array(z.object({ id: z.string().uuid() })),
});
export type PartnerReport = z.infer<typeof PartnerReport>;

/** GET /p/:token（ログインなし） */
/**
 * 協力会社のページで使う会社の言葉（#142 段5）。ログインなしのページは Me が無いので、公開の API がその会社の分を返す。
 * 返すのは画面の文に要る4つだけ（{schedule}・{task}・{siteShort}・{contractor}）
 */
export const PartnerPageLabels = Labels.pick({ schedule: true, task: true, siteShort: true, contractor: true });
export type PartnerPageLabels = z.infer<typeof PartnerPageLabels>;

export const PartnerPage = z.object({
  /** 会社の製品（ロゴと {pw:…} の言い回し。#142 段5） */
  product: Product,
  /** 会社の言葉（#142 段5） */
  labels: PartnerPageLabels,
  company: z.object({ name: z.string() }),
  partner: z.object({ name: z.string() }),
  expiresAt: z.string(),
  today: z.string(),
  show: z.object({ address: z.boolean(), contact: z.boolean() }),
  tasks: z.object({ today: z.array(PartnerTask), next: z.array(PartnerTask), done: z.array(PartnerTask) }),
  reports: z.array(PartnerReport),
  /** 今日あと何件・何枚送れるか */
  left: z.object({ reports: z.number().int(), photos: z.number().int() }),
});
export type PartnerPage = z.infer<typeof PartnerPage>;

/** 使えないリンク（410）の答えに足すもの：会社名と電話（リンクが見つかったときだけ） */
export const PartnerLinkGone = z.object({ state: z.enum(["revoked", "expired"]), company: z.object({ name: z.string(), phone: z.string().nullable() }) });
export type PartnerLinkGone = z.infer<typeof PartnerLinkGone>;

/* ---------------- 会社の画面の報告（ログインあり） ---------------- */

export const ScheduleReportCount = z.object({ pending: z.number().int() });
export type ScheduleReportCount = z.infer<typeof ScheduleReportCount>;
export const ScheduleReportDismiss = z.object({ note: z.string().trim().max(PARTNER_LINK_LIMITS.note).nullable().default(null) });
export type ScheduleReportDismiss = z.input<typeof ScheduleReportDismiss>;
/** POST /schedule/reports/:id/apply の答え */
export const ScheduleReportApplied = z.object({ report: ScheduleReport, groupId: z.string().uuid().nullable(), scheduleId: z.string().uuid(), version: z.number().int() });
export type ScheduleReportApplied = z.infer<typeof ScheduleReportApplied>;

/* ============================================================================
 * 計算（API と画面が同じものを使う）
 * ========================================================================== */

/** 反映の計算に要るもの（報告の中身） */
export type ReportInput = {
  kind: ScheduleReportKind;
  rowId: string | null;
  partnerId: string;
  workDate: string;
  delayDays?: number | null;
  reason?: ScheduleReportReason | null;
};
/** 中止も持った工程表（反映は中止も変える） */
export type ReportDoc = ScheduleDoc & { stops: ScheduleStop[] };

/**
 * 反映できないか（docs/39 §6.2 の8）：工程が消えた・工程でない・担当がその業者でない・もう済み（写真のほか）。
 * 「反映できません（工程が変わりました）」＝409 REPORT_STALE
 */
export function reportStale(doc: Pick<ScheduleDoc, "rows">, r: Pick<ReportInput, "kind" | "rowId" | "partnerId">): boolean {
  const row = r.rowId ? doc.rows.find((x) => x.id === r.rowId) : undefined;
  if (!row || row.kind !== "task") return true;
  if (row.assignee?.type !== "partner" || row.assignee.id !== r.partnerId) return true;
  if (r.kind !== "photo" && (row.progress ?? 0) >= 100) return true;
  return false;
}

/** その工程のその日を止めた暦（工程を選んだ中止と同じ。#105） */
function withRowStop(cal: WorkCalendar, rowId: string, date: string): WorkCalendar {
  const rowStops = new Map<string, ReadonlySet<string>>(cal.rowStops ?? []);
  rowStops.set(rowId, new Set([...(rowStops.get(rowId) ?? []), date]));
  return { ...cal, rowStops };
}

/**
 * 報告を反映した工程表（docs/39 §6.2 の6・7）。案件の工程表で同じことをしたときと同じ計算（後ろの工程は押し出す）：
 *   終わった＝進み 100%／雨で中止＝その日をその工程だけ中止（工程を選んだ中止に足す）／遅れる＝日数＋n／写真＝変えない。
 *   反映できない報告（reportStale）は変えずに返す（API は先に 409 にする）
 */
export function applyReport(doc: ReportDoc, r: ReportInput, cal: WorkCalendar): ReportDoc {
  if (reportStale(doc, r) || r.kind === "photo") return doc;
  const id = r.rowId!;
  if (r.kind === "stop") {
    const stops = addStops(doc.stops, r.workDate, r.workDate, "rain", null, { scope: "rows", rowIds: [id] });
    const n = normalizeSchedule({ rows: doc.rows, links: doc.links }, withRowStop(cal, id, r.workDate));
    return { rows: n.rows, links: n.links, stops };
  }
  const rows = doc.rows.map((x): ScheduleRow => {
    if (x.id !== id) return x;
    // 終わった＝完了・完了日は報告の日（#292。％ ON の会社も同じ＝100% にした日）
    if (r.kind === "done") return setTaskState(x, "done", r.workDate);
    return { ...x, days: Math.min(SCHEDULE_LIMITS.workDays, (x.days ?? 1) + Math.max(1, r.delayDays ?? 1)) };
  });
  const n = normalizeSchedule({ rows, links: doc.links }, cal);
  return { rows: n.rows, links: n.links, stops: doc.stops };
}

/** こうなります（反映の前に見せる1行の材料） */
export type ReportEffect = {
  kind: ScheduleReportKind;
  stale: boolean;
  /** その工程の終わり（前 → 後） */
  rowEnd: { before: string | null; after: string | null };
  /** 最後の節目（無ければ null）の日（前 → 後） */
  lastMilestone: { name: string; before: string; after: string } | null;
  /** 押された（終わりの日が変わった）ほかの工程の数 */
  pushed: number;
  /** その工程が延びる稼働日の数（雨で中止・遅れる） */
  extended: number;
};
export function reportEffect(doc: ReportDoc, r: ReportInput, cal: WorkCalendar): ReportEffect {
  const stale = reportStale(doc, r);
  const before = doc.rows.find((x) => x.id === r.rowId);
  const after = stale ? doc : applyReport(doc, r, cal);
  const a = after.rows.find((x) => x.id === r.rowId);
  const lastOf = (rows: readonly ScheduleRow[]) => {
    const ms = rows.filter((x) => x.kind === "milestone" && x.start).sort((p, q) => (p.start! < q.start! ? -1 : p.start! > q.start! ? 1 : 0));
    return ms[ms.length - 1] ?? null;
  };
  const m0 = lastOf(doc.rows);
  const m1 = m0 ? after.rows.find((x) => x.id === m0.id) : null;
  const b = new Map(doc.rows.map((x) => [x.id, x]));
  const pushed = after.rows.filter((x) => x.id !== r.rowId && x.kind === "task" && x.end && b.get(x.id)?.end && b.get(x.id)!.end !== x.end).length;
  let extended = 0;
  if (!stale && before?.end && a?.end && a.end > before.end) {
    const rc = calendarForRow(a, r.kind === "stop" && r.rowId ? withRowStop(cal, r.rowId, r.workDate) : cal);
    extended = workDaysBetween(addDays(before.end, 1), a.end, rc);
  }
  return {
    kind: r.kind,
    stale,
    rowEnd: { before: before?.end ?? null, after: a?.end ?? null },
    lastMilestone: m0?.start && m1?.start ? { name: m0.name, before: m0.start, after: m1.start } : null,
    pushed,
    extended,
  };
}
/** こうなります の文（R1・案件の工程表の吹き出し）。写真はひとこと（画面が小さい絵と並べる） */
export function reportEffectText(r: ReportInput & { note?: string | null }, e: ReportEffect, pct = true): string {
  if (e.stale) return PARTNER_LINK_MSG.stale;
  const tail = () => {
    if (e.lastMilestone && e.lastMilestone.before !== e.lastMilestone.after) return `→ ${e.lastMilestone.name || "節目"} ${mdOf(e.lastMilestone.before)} → ${mdOf(e.lastMilestone.after)}`;
    if (e.rowEnd.before && e.rowEnd.after && e.rowEnd.before !== e.rowEnd.after) return `→ 終わり ${mdOf(e.rowEnd.before)} → ${mdOf(e.rowEnd.after)}`;
    return "→ 日付は変わりません";
  };
  switch (r.kind) {
    case "done":
      // ％ OFF の会社（#292）は「完了（完了日 11/3）」
      return pct ? "進捗を 100% に" : `完了（完了日 ${mdOf(r.workDate)}）`;
    case "stop":
      return e.extended ? `${mdOf(r.workDate)} をこの{task}のみ中止 → ${e.extended}日延長` : `${mdOf(r.workDate)} をこの{task}のみ中止 → 日付は変わりません`;
    case "delay":
      return `${r.delayDays ?? 0}日延長（${r.reason ? REPORT_REASON_LABEL[r.reason] : "理由なし"}）${tail()}`;
    case "photo":
      return r.note ? `「${r.note}」` : "";
  }
}

/* ---------------- 協力会社のページの分け方 ---------------- */

export type PartnerTaskRow = { id: string; kind: ScheduleRowKind; start: string | null; end: string | null; days: number | null; progress: number | null; workOffDays?: boolean };
export type PartnerTaskInfo = {
  workDates: string[];
  dayIndex: number | null;
  lastDay: boolean;
  overdue: boolean;
  started: boolean;
  workToday: boolean;
  /** 始まっていて終わっていない{task}で今日が休み（#293） */
  restToday: boolean;
  nextDate: string | null;
};
/**
 * 協力会社のページの 今日・次・済み（docs/39 §6.3.1、受け入れ ⑥）。日はそれぞれの工程表の暦（休み・中止・「休日も」）で数える：
 *   今日＝今日がその工程の稼働日（今日で終わり・n日目。今日「終わった」を反映して済みになった工程も、結果が見えるよう今日に残す）。
 *     終わりの日を過ぎて済んでいない工程も今日に（「予定は 10/6 まで」。30日前まで）
 *   次＝済んでいない工程で、今日より後に稼働日があるもの（最初の稼働日が 60日先まで。始まっていて今日が休みの工程も）。並びは次の稼働日の順
 *   済み＝進み 100% で、終わりが 7日前まで。並びは終わりの新しい順
 */
export function partnerTasksOf<T extends { row: PartnerTaskRow; cal: WorkCalendar }>(
  items: readonly T[],
  today: string,
  opts: { nextDays?: number; doneDays?: number; overdueDays?: number } = {},
): { today: (T & PartnerTaskInfo)[]; next: (T & PartnerTaskInfo)[]; done: (T & PartnerTaskInfo)[] } {
  const nextDays = opts.nextDays ?? PARTNER_LINK_LIMITS.nextDays;
  const doneDays = opts.doneDays ?? PARTNER_LINK_LIMITS.doneDays;
  const overdueDays = opts.overdueDays ?? PARTNER_LINK_LIMITS.overdueDays;
  const out = { today: [] as (T & PartnerTaskInfo)[], next: [] as (T & PartnerTaskInfo)[], done: [] as (T & PartnerTaskInfo)[] };
  for (const it of items) {
    const r = it.row;
    if (r.kind !== "task" || !r.start || !r.end) continue;
    const workDates = workDatesOf({ kind: r.kind, id: r.id, start: r.start, end: r.end, workOffDays: r.workOffDays }, it.cal);
    const k = workDates.indexOf(today);
    const nextDate = workDates.find((d) => d > today) ?? null;
    const info: PartnerTaskInfo = {
      workDates,
      dayIndex: k >= 0 ? k + 1 : null,
      lastDay: k >= 0 && k === workDates.length - 1,
      overdue: r.end < today && (r.progress ?? 0) < 100,
      started: r.start <= today,
      workToday: k >= 0,
      restToday: k < 0 && r.start <= today && r.end >= today && (r.progress ?? 0) < 100,
      nextDate,
    };
    const x = { ...it, ...info };
    if (k >= 0) out.today.push(x);
    else if ((r.progress ?? 0) >= 100) {
      if (r.end >= addDays(today, -doneDays)) out.done.push(x);
    } else if (info.overdue && r.end >= addDays(today, -overdueDays)) out.today.push(x);
    else if (nextDate && dayNo(nextDate) - dayNo(today) <= nextDays) out.next.push(x);
  }
  const by = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  out.today.sort((a, b) => Number(a.overdue) - Number(b.overdue) || by(a.row.start!, b.row.start!));
  out.next.sort((a, b) => by(a.nextDate!, b.nextDate!) || by(a.row.start!, b.row.start!));
  out.done.sort((a, b) => by(b.row.end!, a.row.end!));
  return out;
}

/** 案件の工程表の最後の日（遅れる報告の見込みに使う。無ければ null） */
export const scheduleEndOf = (rows: readonly ScheduleRow[]): string | null => scheduleBriefOf(rows).end;
